import React, { createContext, useContext } from 'react';

import { SessionLockModal } from '../components/SessionLockModal';
import type { SsoContextValue, SsoConfig } from '../types';
import { useSsoSession } from '../hooks/useSsoSession';

export const SsoContext = createContext<SsoContextValue | undefined>(undefined);

export interface SsoProviderProps {
  config: SsoConfig;
  children: React.ReactNode;
}

export const SsoProvider: React.FC<SsoProviderProps> = ({
  config,
  children,
}) => {
  const value = useSsoSession(config);

  return (
    <SsoContext.Provider value={value}>
      {value.sessionPhase === 'locked' && (
        <SessionLockModal
          copy={config.sessionLockCopy}
          onLogout={value.singleSignOff}
        />
      )}
      {children}
    </SsoContext.Provider>
  );
};

export const useSso = (): SsoContextValue => {
  const context = useContext(SsoContext);

  if (!context) throw new Error('useSso must be used within an SsoProvider');

  return context;
};

/** Returns SSO context when inside SsoProvider; null on public/unauthenticated routes. */
export const useSsoOptional = (): SsoContextValue | null => {
  return useContext(SsoContext) ?? null;
};
